import { Card, Badge } from 'react-bootstrap';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { ProductData } from '../models/types';
import { AlertBadge } from './AlertBadge';
import { detectAlerts } from '../models/types';

interface ProductCardProps {
  readonly product: ProductData;
  readonly isSelected: boolean;
  readonly index: number;
  readonly competitorCount: number;
  readonly onSelect: () => void;
  readonly isVariation?: boolean;
  readonly isExpanded?: boolean;
  readonly onToggleExpand?: () => void;
}

export function ProductCard({
  product,
  isSelected,
  index,
  competitorCount,
  onSelect,
  isVariation = false,
  isExpanded = false,
  onToggleExpand,
}: ProductCardProps) {
  const skinSeoul = product.skinSeoulProduct;
  const hasVariations = !isVariation && skinSeoul.type === 'Variable' && (skinSeoul.variations?.length || 0) > 0;

  const allAlerts = product.machineMatchedProducts.flatMap((comp) =>
    comp.competitorProducts.flatMap((cp) => detectAlerts(skinSeoul, cp as any)),
  );

  const priceAnomalyAlerts = allAlerts.filter((alert) => alert.type === 'price_anomaly');
  const uniqueAlerts = priceAnomalyAlerts.filter(
    (alert, idx, arr) => arr.findIndex((a) => a.type === alert.type) === idx,
  );

  const variationCount = skinSeoul.variations?.length || 0;

  return (
    <Card
      className={`mb-2 border-0 ${isSelected ? 'border-primary border-2' : ''}`}
      onClick={onSelect}
      style={{
        cursor: 'pointer',
        backgroundColor: isSelected ? '#f0f7ff' : isVariation ? '#fafafa' : 'white',
        transition: 'all 0.2s ease',
        borderRadius: '8px',
        boxShadow: isVariation ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
        marginLeft: isVariation ? '24px' : 0,
        borderLeft: isVariation ? '2px solid #dee2e6' : 'none',
      }}
    >
      <Card.Body className="p-2">
        <div className="d-flex gap-2">
          {hasVariations && (
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: '20px', cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand?.();
              }}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </div>
          )}
          {!isVariation && (
            <div className="position-relative" style={{ minWidth: '60px' }}>
              {skinSeoul.productImageUrls && skinSeoul.productImageUrls.length > 0 ? (
                <img
                  src={skinSeoul.productImageUrls[0]}
                  alt={skinSeoul.name}
                  style={{
                    width: '60px',
                    height: '60px',
                    objectFit: 'contain',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                  }}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center text-muted"
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '0.65rem',
                  }}
                >
                  No img
                </div>
              )}
              <Badge
                bg="dark"
                className="position-absolute"
                style={{
                  top: '-6px',
                  left: '-6px',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  fontSize: '0.65rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {index + 1}
              </Badge>
            </div>
          )}

          <div className="flex-grow-1 min-width-0">
            <div className="d-flex justify-content-between align-items-start mb-1">
              <h6
                className="mb-0 text-truncate"
                style={{ fontSize: isVariation ? '0.8rem' : '0.85rem', lineHeight: '1.3', maxWidth: '250px' }}
                title={skinSeoul.name}
              >
                {isVariation && <span className="text-muted me-1">└</span>}
                {skinSeoul.name}
              </h6>
              <div className="d-flex gap-1">
                {!isVariation && variationCount > 0 && (
                  <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                    {variationCount} var
                  </Badge>
                )}
                {competitorCount > 0 && (
                  <Badge bg="info" style={{ fontSize: '0.65rem', flexShrink: 0 }}>
                    {competitorCount} competitor{competitorCount !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
            </div>

            <div className="d-flex gap-2 flex-wrap mb-1">
              <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                SKU: {skinSeoul.sku}
              </small>
              {!isVariation && (
                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                  {skinSeoul.brand}
                </small>
              )}
            </div>

            <div className="d-flex gap-2 flex-wrap mb-1">
              {skinSeoul.salePrice != null && (
                <small className="fw-bold" style={{ fontSize: '0.75rem', color: '#0d6efd' }}>
                  ${skinSeoul.salePrice.toFixed(2)}
                </small>
              )}
              {skinSeoul.originalPrice != null && skinSeoul.originalPrice !== skinSeoul.salePrice && (
                <small className="text-decoration-line-through text-muted" style={{ fontSize: '0.7rem' }}>
                  ${skinSeoul.originalPrice.toFixed(2)}
                </small>
              )}
              {skinSeoul.cogs != null && (
                <small style={{ fontSize: '0.7rem', color: '#6c757d' }}>
                  COGS: ₩{skinSeoul.cogs.toLocaleString()}
                </small>
              )}
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-1 flex-wrap">
                {skinSeoul.type && !isVariation && (
                  <Badge
                    bg={skinSeoul.type === 'Simple' ? 'success' : 'warning'}
                    style={{ fontSize: '0.6rem' }}
                  >
                    {skinSeoul.type}
                  </Badge>
                )}
                {skinSeoul.size && (
                  <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                    {skinSeoul.size}
                  </Badge>
                )}
                {skinSeoul.pack && (
                  <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                    {skinSeoul.pack}
                  </Badge>
                )}
              </div>
              <AlertBadge alerts={uniqueAlerts} size="sm" />
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
